import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { api, type Allocation, type Category, type EntryDetail, type EntrySummary, type PropertySummary, type VehicleSummary } from "../api";
import { ErrorNotice, Loading } from "../ui";

const AREA_LABELS: Record<string, string> = {
  work: "Work",
  work_car: "Car used for work",
  work_from_home: "Working from home",
  rental: "Rental property",
  shares: "Shares and investments",
  super_contributions: "Super",
  other: "Other",
  job: "Income",
  bank_interest: "Income",
  other_income: "Income",
};

const TARGETS: { value: Allocation["targetType"]; label: string; labelHint?: string }[] = [
  { value: "work", label: "Work" },
  { value: "vehicle", label: "A car used for work", labelHint: "Which car?" },
  { value: "rental", label: "A rental property", labelHint: "Which property?" },
  { value: "investment", label: "Shares or investments" },
  { value: "asset", label: "An asset to depreciate", labelHint: "What is it?" },
  { value: "private", label: "Personal (not claimed)" },
  { value: "other", label: "Other" },
];

const toCents = (text: string): number | null => {
  const clean = text.replace(/[$,\s]/g, "");
  if (!clean) return null;
  const value = Number(clean);
  return Number.isFinite(value) && value >= 0 ? Math.round(value * 100) : NaN;
};
const toDollars = (cents: number | null) => (cents === null ? "" : (cents / 100).toFixed(2));

export function Review() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const [entry, setEntry] = useState<EntryDetail | null>(null);
  const [categories, setCategories] = useState<Category[]>([]);
  const [error, setError] = useState<unknown>(null);
  const [busy, setBusy] = useState(false);

  // Form state
  const [supplier, setSupplier] = useState("");
  const [abn, setAbn] = useState("");
  const [date, setDate] = useState("");
  const [amount, setAmount] = useState("");
  const [gst, setGst] = useState("");
  const [description, setDescription] = useState("");
  const [categoryCode, setCategoryCode] = useState("");
  const [target, setTarget] = useState<Allocation["targetType"] | "">("");
  const [targetLabel, setTargetLabel] = useState("");
  const [targetId, setTargetId] = useState("");
  const [cars, setCars] = useState<VehicleSummary[]>([]);
  const [properties, setProperties] = useState<PropertySummary[]>([]);
  const [claimPct, setClaimPct] = useState("100");
  const [remember, setRemember] = useState(true);

  useEffect(() => {
    setEntry(null);
    Promise.all([api.get<EntryDetail>(`/api/entries/${id}`), api.get<Category[]>("/api/categories"), api.get<VehicleSummary[]>("/api/vehicles"), api.get<PropertySummary[]>("/api/properties")])
      .then(([e, c, v, p]) => {
        setProperties(p.filter((x) => !x.soldOn || x.id === e.allocations[0]?.targetId));
        setEntry(e);
        setCategories(c);
        setCars(v.filter((car) => !car.disposedOn || car.id === e.allocations[0]?.targetId));
        setSupplier(e.supplier ?? "");
        setAbn(e.abn ?? "");
        setDate(e.date ?? "");
        setAmount(toDollars(e.amountCents));
        setGst(toDollars(e.gstCents));
        setDescription(e.description ?? "");
        setCategoryCode(e.categoryCode ?? "");
        const first = e.allocations[0];
        setTarget(first?.targetType ?? "");
        setTargetLabel(first?.label ?? "");
        setTargetId(first?.targetId ?? "");
        setClaimPct(String(Math.round((first?.claimPct ?? 1) * 100)));
      })
      .catch(setError);
  }, [id]);

  const grouped = useMemo(() => {
    const groups = new Map<string, Category[]>();
    for (const c of categories) {
      const label = c.kind === "income" ? "Income" : AREA_LABELS[c.area] ?? "Other";
      groups.set(label, [...(groups.get(label) ?? []), c]);
    }
    return [...groups.entries()];
  }, [categories]);

  if (!entry) return <><ErrorNotice error={error} /><Loading /></>;

  const read = entry.read?.fields ?? {};
  const uncertain = (field: string, current: string) => {
    const r = read[field];
    return r && r.confidence !== "high" && current !== "" ? " uncertain" : "";
  };
  const category = categories.find((c) => c.code === categoryCode);
  const isIncome = category?.kind === "income";
  const targetInfo = TARGETS.find((t) => t.value === target);
  // Rent and rental expenses are filed to a property, even when "What's it for?" is left to the category.
  const forProperty = target === "rental" || (!target && category?.area === "rental");
  const pickedId = (target === "vehicle" || forProperty) && targetId ? targetId : null;
  const confirmed = entry.status === "confirmed";

  const payload = () => {
    const amountCents = toCents(amount);
    const gstCents = toCents(gst);
    if (Number.isNaN(amountCents) || Number.isNaN(gstCents)) throw new Error("Amounts must be numbers, like 84.16");
    const pct = Number(claimPct);
    if (!isIncome && target && (!Number.isFinite(pct) || pct < 0 || pct > 100)) throw new Error("The work-use percentage must be between 0 and 100");
    return {
      supplier: supplier.trim() || null,
      abn: abn.replace(/\s/g, "") || null,
      date: date || null,
      amountCents,
      gstCents,
      description: description.trim() || null,
      categoryCode: categoryCode || null,
      kind: isIncome ? "income" : "expense",
      ...(target || (forProperty && pickedId)
        ? { allocations: [{
            targetType: target || "rental",
            targetId: pickedId,
            label: pickedId ? (cars.find((c) => c.id === pickedId)?.name ?? properties.find((p) => p.id === pickedId)?.name ?? null) : targetLabel.trim() || null,
            share: 1,
            claimPct: target === "private" ? 0 : isIncome ? 1 : pct / 100,
          }] }
        : {}),
    };
  };

  const run = async (work: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    try {
      await work();
    } catch (e) {
      setError(e);
    } finally {
      setBusy(false);
    }
  };

  const goToNext = async () => {
    const drafts = await api.get<EntrySummary[]>("/api/entries?status=draft");
    const next = drafts.find((d) => d.id !== entry.id);
    navigate(next ? `/inbox/${next.id}` : "/inbox");
  };

  return (
    <div className="stack" style={{ "--gap": "20px" } as React.CSSProperties}>
      <div className="row wrap">
        <Link to={confirmed ? "/records" : "/inbox"} className="btn ghost">← Back</Link>
        <div className="spacer" />
        {confirmed && <span className="pill ok">Saved{entry.autoConfirmed ? " automatically" : ""}</span>}
      </div>
      {params.get("duplicate") && <div className="notice warn">You've already uploaded that file — here's the existing record.</div>}

      <div className="review">
        <div className="card preview">
          {entry.document ? (
            entry.document.mime === "application/pdf"
              ? <iframe title="Document" src={entry.document.url} />
              : entry.document.mime === "image/heic"
                ? <div className="empty">HEIC photos can't be previewed in this browser. <a href={entry.document.url}>Download the original</a>.</div>
                : <img alt="Uploaded document" src={entry.document.url} />
          ) : <div className="empty">No document attached.</div>}
        </div>

        <form
          className="card stack"
          style={{ "--gap": "16px" } as React.CSSProperties}
          onSubmit={(event) => {
            event.preventDefault();
            void run(async () => {
              await api.patch(`/api/entries/${entry.id}`, payload());
              if (confirmed) {
                navigate("/records");
                return;
              }
              await api.post(`/api/entries/${entry.id}/confirm`, { rememberSupplier: remember });
              await goToNext();
            });
          }}
        >
          <div>
            <h2>{confirmed ? "Record" : "Check and save"}</h2>
            {entry.read && Object.keys(read).length > 0 && !confirmed && (
              <p className="muted small">We've filled in what we could read. Fields highlighted in amber are worth a second look.</p>
            )}
            {entry.read && Object.keys(read).length === 0 && (
              <p className="muted small">We couldn't read text from this document, so please fill in the details.</p>
            )}
            {entry.supplierRule && !confirmed && (
              <p className="small" style={{ marginTop: 6 }}>
                <span className="pill ok">Remembered</span>{" "}
                Filed the same way as {entry.supplierRule.name} before ({entry.supplierRule.timesConfirmed}×).
              </p>
            )}
          </div>

          <div className={`field${uncertain("supplier", supplier)}`}>
            <label htmlFor="supplier">Supplier</label>
            <input id="supplier" type="text" value={supplier} onChange={(e) => setSupplier(e.target.value)} />
          </div>
          <div className="fields-2">
            <div className={`field${uncertain("date", date)}`}>
              <label htmlFor="date">Date</label>
              <input id="date" type="date" value={date} onChange={(e) => setDate(e.target.value)} required />
            </div>
            <div className="field">
              <label htmlFor="abn">ABN <span className="muted">(optional)</span></label>
              <input id="abn" type="text" inputMode="numeric" value={abn} onChange={(e) => setAbn(e.target.value)} />
            </div>
          </div>
          <div className="fields-2">
            <div className={`field${uncertain("totalCents", amount)}`}>
              <label htmlFor="amount">Total paid</label>
              <input id="amount" type="text" inputMode="decimal" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="0.00" required />
            </div>
            <div className={`field${uncertain("gstCents", gst)}`}>
              <label htmlFor="gst">GST included <span className="muted">(optional)</span></label>
              <input id="gst" type="text" inputMode="decimal" value={gst} onChange={(e) => setGst(e.target.value)} placeholder="0.00" />
            </div>
          </div>

          <div className="field">
            <label htmlFor="category">Category</label>
            <select id="category" value={categoryCode} onChange={(e) => setCategoryCode(e.target.value)} required>
              <option value="">Choose a category…</option>
              {grouped.map(([group, items]) => (
                <optgroup key={group} label={group}>
                  {items.map((c) => <option key={c.code} value={c.code}>{c.name}</option>)}
                </optgroup>
              ))}
            </select>
            {category?.help && <span className="hint">{category.help}</span>}
          </div>

          {!isIncome && (
            <div className="field">
              <label htmlFor="target">What's it for?</label>
              <select id="target" value={target} onChange={(e) => setTarget(e.target.value as Allocation["targetType"])}>
                <option value="">Decide from the category</option>
                {TARGETS.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
              </select>
            </div>
          )}
          {target === "vehicle" && cars.length > 0 && (
            <div className="field">
              <label htmlFor="car">Which car?</label>
              <select id="car" value={targetId} onChange={(e) => {
                setTargetId(e.target.value);
                const car = cars.find((c) => c.id === e.target.value);
                if (car?.businessPct != null) setClaimPct(String(car.businessPct));
              }}>
                <option value="">Choose a car…</option>
                {cars.map((c) => <option key={c.id} value={c.id}>{c.name}{c.businessPct != null ? ` (${c.businessPct}% business)` : ""}</option>)}
              </select>
              <span className="hint">Its logbook % is used for the claim. <a href="/cars">Manage cars</a></span>
            </div>
          )}
          {forProperty && properties.length > 0 && (
            <div className="field">
              <label htmlFor="property">Which property?</label>
              <select id="property" value={targetId} onChange={(e) => setTargetId(e.target.value)}>
                <option value="">Choose a property…</option>
                {properties.map((p) => <option key={p.id} value={p.id}>{p.name}{p.yourSharePct < 100 ? ` (you own ${p.yourSharePct}%)` : ""}</option>)}
              </select>
              <span className="hint">Enter the bill in full — your share is worked out from the ownership. <a href="/properties">Manage properties</a></span>
            </div>
          )}
          {targetInfo?.labelHint && !(target === "vehicle" && cars.length > 0) && !(forProperty && properties.length > 0) && (
            <div className="field">
              <label htmlFor="target-label">{targetInfo.labelHint}</label>
              <input id="target-label" type="text" value={targetLabel} onChange={(e) => setTargetLabel(e.target.value)} placeholder="A name you'll recognise" />
              <span className="hint">Once you've added your cars and properties, you'll pick them from a list.</span>
            </div>
          )}
          {!isIncome && target && target !== "private" && (
            <div className="field" style={{ maxWidth: 220 }}>
              <label htmlFor="claim">Work or business use</label>
              <div className="row">
                <input id="claim" type="number" min={0} max={100} value={claimPct} onChange={(e) => setClaimPct(e.target.value)} />
                <span>%</span>
              </div>
              <span className="hint">The share you can claim, e.g. your logbook percentage.</span>
            </div>
          )}

          <div className="field">
            <label htmlFor="description">Note <span className="muted">(optional)</span></label>
            <textarea id="description" rows={2} value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>

          {!confirmed && (
            <label className="row small" style={{ gap: 8 }}>
              <input type="checkbox" checked={remember} onChange={(e) => setRemember(e.target.checked)} />
              Remember this for future receipts from this supplier
            </label>
          )}

          <ErrorNotice error={error} />
          <div className="row wrap">
            {!confirmed && (
              <button type="button" className="btn ghost danger" disabled={busy}
                onClick={() => run(async () => {
                  if (!window.confirm("Discard this entry? The document stays in your records.")) return;
                  await api.delete(`/api/entries/${entry.id}`);
                  navigate("/inbox");
                })}>Discard</button>
            )}
            <div className="spacer" />
            {!confirmed && (
              <button type="button" className="btn" disabled={busy}
                onClick={() => run(async () => { await api.patch(`/api/entries/${entry.id}`, payload()); navigate("/inbox"); })}>
                Save for later
              </button>
            )}
            <button type="submit" className="btn primary" disabled={busy}>{confirmed ? "Save changes" : "Save"}</button>
          </div>
        </form>
      </div>
    </div>
  );
}
